/**
 * 들어온 순간의 화면 — 화면 상세가 한 장씩 집고, 사업장 현황의 화면 그림(`DisplayThumbnail`)이 같은 규칙으로 건다.
 *
 * 화면을 따라 그림을 계속 바꾸지 않는다. 들어온 순간 한 번 집어 멈춰 두고, 다시 찍을 때만 새로 집는다. 따라가려면
 * 5초마다 새 보고가 있어야 하는데, 조회가 곧 "보고 있다" 는 표식이라 탭이 열려 있는 내내 화면이 자기 화면을 찍는다.
 *
 * 2026-10-22 화면 멀티뷰를 지우면서 벽 전체를 한꺼번에 집던 도우미(`mergeWallCaptures`·따로 찍기 계획 등)는 뺐다.
 */
import type { DisplayPreview, ScreenReport, WallCard } from '../api/client.js';
import { decideScreenView } from './LiveScreen.js';

/**
 * 이만큼 앞서 찍힌 보고까지는 "들어온 순간의 화면" 으로 받는다.
 *
 * 다른 곳에서 이미 보고 있었다면 화면은 5초마다 보고하고 있다. 들어온 뒤에 찍힌 것만
 * 받으면 그때도 매번 몇 초씩 빈 그림을 보게 된다. 보고 주기의 두 배를 둬서 한 번
 * 늦은 보고까지 받는다. 그보다 오래된 것은 들어오기 전의 화면이다.
 */
export const CAPTURE_FRESH_MS = 10_000;

/**
 * 캡처를 기다리는 한도.
 *
 * 아무도 보고 있지 않던 화면은 다음 편성 조회(기본 30초)에서야 찍으라는 말을 듣고,
 * 그 뒤 5초 안에 찍는다. 그보다 넉넉히 기다린다. 넘기면 찍어 보내지 못하는 화면
 * (셸 없이 템플릿·웹 페이지를 띄운 브라우저 키오스크 등)으로 보고 편성 기준 그림으로
 * 물러난다 — 끝없이 기다리면 그동안 이 테넌트의 화면 전부가 계속 자기 화면을 찍는다.
 */
export const CAPTURE_WAIT_MS = 60_000;

/**
 * 이 조회 결과에서 캡처로 받을 보고. 받을 것이 없으면 null.
 *
 * **보고의 나이는 같은 응답의 서버 시각으로 잰다.** 둘 다 서버 시계라, 콘솔을 띄운
 * PC 의 시계가 틀어져 있어도 판단이 흔들리지 않는다.
 *
 * `after` 는 다시 찍을 때 직전 캡처의 시각이다. 그보다 새로 찍힌 보고만 받는다 —
 * 안 그러면 방금 받은 응답에 든 같은 그림을 "새로 찍었다" 며 다시 건다.
 */
export function takeCapture(
  preview: Pick<DisplayPreview, 'at' | 'screen'>,
  after: string | null,
): ScreenReport | null {
  const screen = preview.screen;
  if (!screen) return null;
  const takenAt = Date.parse(screen.taken_at);
  if (Number.isNaN(takenAt)) return null;
  if (after !== null && takenAt <= Date.parse(after)) return null;
  return Date.parse(preview.at) - takenAt <= CAPTURE_FRESH_MS ? screen : null;
}

/** 화면 한 대에 집어 둔 그림. */
export interface WallCapture {
  report: ScreenReport;
  /**
   * 영상이면 그 장면을 꺼낼 영상 주소. **집는 순간에 함께 붙든다.**
   *
   * 화면의 `now_playing` 은 조회마다 바뀐다. 그릴 때 그것을 보면, 화면이 다음 항목으로
   * 넘어간 뒤에는 **다른 영상에서** 찍은 위치의 장면을 꺼내게 된다.
   */
  videoUrl: string | null;
}

/**
 * 영상 보고의 장면을 꺼낼 주소.
 *
 * 지금 나가는 것이 찍은 자산과 같을 때만 쓴다. 보고에 자산이 비어 있으면 지금 나가는
 * 것으로 본다 — 그 밖에 기댈 것이 없다.
 */
export function captureVideoUrl(
  report: ScreenReport,
  playing: WallCard['now_playing'],
): string | null {
  if (report.kind !== 'video' || !playing || playing.type !== 'video') return null;
  if (report.asset_id !== null && report.asset_id !== playing.asset_id) return null;
  return playing.preview_url;
}

/**
 * 들어온 순간의 캡처가 오기 전까지 대신 걸 **이전 모습** — 서버가 준 마지막 보고. 걸 수 없으면 null.
 *
 * 캡처는 20~40초 걸린다. 아무도 보지 않던 화면은 다음 편성 조회(기본 30초)에서야 찍으라는
 * 말을 듣기 때문이다. 그동안 빈 자리를 두면 콘솔이 멈춘 것으로 읽힌다. 그래서 가진 그림을
 * 먼저 걸되, 부르는 쪽이 찍은 시각을 함께 밝혀 지금 화면으로 읽히지 않게 한다. 캡처가
 * 오면 그것으로 바뀐다.
 *
 * - `gone` 은 그림이 서버에서 이미 지워진 보고의 시각이다(그새 화면이 새로 올렸다).
 *   그 보고를 다시 걸면 검은 상자만 남는다.
 * - 영상 보고인데 그 장면을 꺼낼 주소가 없으면 걸지 않는다. 몇 분 전 재생 위치를 글자로
 *   남기느니 편성으로 그리는 편이 낫다.
 */
export function earlierScreen(
  report: ScreenReport | null | undefined,
  videoUrl: string | null,
  gone: string | null,
): ScreenReport | null {
  if (!report || report.taken_at === gone) return null;
  return decideScreenView(report, videoUrl) === 'status' ? null : report;
}
